import { useState, useEffect, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Auth } from "shared/@auth/Auth";
import { useLocation } from "react-router-dom";

// Hook for GET operations
export function useAgendaQueries(options = {}) {
  const { makeAuthedRequest } = Auth.useContainer();
  const location = useLocation();

  function pathParam(location) {
    return location.includes("executive-committee") ? "EC" : "MT";
  }

  // Only run the archive agenda query if explicitly enabled
  const enableArchiveAgenda = !!options.enableArchiveAgenda;

  const {
    data: archiveAgendaData,
    refetch: refetchArchive,
    status: archiveStatus,
  } = useQuery(
    ["archiveAgenda", location.pathname],
    () =>
      makeAuthedRequest(
        `/management-team/meetings/${pathParam(location.pathname)}/completed`,
        {
          method: "GET",
        }
      ).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    { enabled: enableArchiveAgenda }
  );

  return {
    archiveAgendaData,
    archiveStatus,
    refetchArchive,
  };
}

// Hook for mutations (POST, PUT, DELETE operations)
export function useAgendaMutations() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();
  const location = useLocation();

  function pathParam(location) {
    return location.includes("executive-committee") ? "EC" : "MT";
  }

  // ADD AGENDA
  const agendaPost = useMutation(
    // Query Key for invalidating data
    ["agendaPost"],
    async (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/add`, {
        method: "POST",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Edit Agenda
  const editAgendaPut = useMutation(
    ["editAgenda"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/edit`, {
        method: "PUT",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return await res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Complete Agenda
  const completeAgendaPut = useMutation(
    ["completeAgenda"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/completed`, {
        method: "PUT",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Delete Agenda
  const deleteAgenda = useMutation(
    ["deleteAgenda"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/delete`, {
        method: "DELETE",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Move Agenda
  const moveAgendaPut = useMutation(
    ["moveAgenda"],
    async (agendaObject) =>
      await makeAuthedRequest(`/management-team/agenda/move`, {
        method: "PUT",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Duplicate Agenda
  const duplicateAgendaPost = useMutation(
    ["duplicateAgenda"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/duplicate`, {
        method: "POST",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Add Action Item
  const actionItemEditPut = useMutation(
    ["actionItemEdit"],
    (actionItemObject) =>
      makeAuthedRequest(`/management-team/actions/edit`, {
        method: "PUT",
        body: JSON.stringify(actionItemObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Get Action Items
  const actionItemsGet = useMutation(() =>
    makeAuthedRequest(
      `/management-team/actions/${pathParam(location.pathname)}/get`,
      {
        method: "GET",
      }
    ).then(async (res) => {
      if (res.ok) {
        return await res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  const actionItemSharePut = useMutation(
    ["actionItemSharePut"],
    (actionItemShareObject) =>
      makeAuthedRequest(
        `/management-team/actions/${pathParam(location.pathname)}/shares`,
        {
          method: "PUT",
          body: JSON.stringify(actionItemShareObject),
        }
      ).then(async (res) => {
        if (res.ok) {
          return await res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  // Agenda Item Order
  const agendaItemOrderPut = useMutation(
    ["agendaItemOrder"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/order`, {
        method: "PUT",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Agenda Item Publish
  const agendaItemPublishPut = useMutation(
    ["agendaItemPublish"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/publish`, {
        method: "PUT",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  const getFile = (url, fileName) => {
    fetch(url)
      .then((response) => response.text())
      .then((res) => {
        // create local url for image and download
        const link = document.createElement("a");
        link.href = res;
        link.setAttribute("download", fileName); //or any other extension
        document.body.appendChild(link);
        window.open(link);
      });
  };

  const fetchPdf = (fileName) =>
    makeAuthedRequest(`/management-team/downloadfile/agendas/${fileName}`, {
      method: "GET",
    }).then((res) => {
      if (res.ok) {
        res.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);
          getFile(url, fileName);
        });
      } else {
        console.warn(res);
        throw new Error(res.message);
      }
    });

  const agendaItemPdfData = async (fileName) =>
    await makeAuthedRequest(
      `/management-team/downloadfile/agendas/${fileName}`,
      {
        method: "GET",
      }
    ).then((res) => {
      if (res.ok) {
        return res;
      } else {
        console.warn(res);
        throw new Error(res.message);
      }
    });

  const postAttendancePDF = async (attendanceItem) => {
    return makeAuthedRequest("management-team/agenda/attendance", {
      method: "put",
      body: JSON.stringify(attendanceItem),
    }).then((res) => {
      if (res.ok) {
        return res;
      } else {
        console.warn(res);
        throw new Error(res.message);
      }
    });
  };

  return {
    // All mutation operations
    agendaPost,
    editAgendaPut,
    completeAgendaPut,
    deleteAgenda,
    moveAgendaPut,
    duplicateAgendaPost,
    actionItemEditPut,
    actionItemsGet,
    actionItemSharePut,
    agendaItemOrderPut,
    agendaItemPublishPut,
    // Utility functions
    fetchPdf,
    agendaItemPdfData,
    postAttendancePDF,
  };
}

// For backward compatibility - combines both hooks
export function useAgenda(options = {}) {
  const queries = useAgendaQueries(options);
  const mutations = useAgendaMutations();

  // Only include archiveAgendaData, archiveStatus, refetchArchive if enabled
  const { archiveAgendaData, archiveStatus, refetchArchive, ...restQueries } =
    queries;

  if (options.enableArchiveAgenda) {
    return {
      archiveAgendaData,
      archiveStatus,
      refetchArchive,
      ...mutations,
      ...restQueries,
    };
  } else {
    return {
      ...mutations,
      ...restQueries,
    };
  }
}
